'use client';

import { useState } from 'react';

import { Button, CheckBox, Skeleton, TextField, Typography } from 'lighthouse';

import ScrollFade from '@/widgets/ScrollFade';

import { formatAttributeOptionLabel, getOptionSelectionKey, optionValueToString } from '@/utilities/attributeFilters';
import { ATTRIBUTE_FILTER_COPY } from '@/utilities/attributeFilters/constants';

import { AttributeCheckboxFilterProps, AttributeOptionItem } from './models';

import { CloseCircle, SearchNormal1 } from 'iconsax-react';

const SEARCH_OPTION_THRESHOLD = 6;

export const AttributeCheckboxFilter = ({
    attribute,
    selection,
    draft,
    optionItems,
    selectedOptionItems,
    remoteSearch,
    onSetValues
}: AttributeCheckboxFilterProps) => {
    const [localQuery, setLocalQuery] = useState('');
    const query = remoteSearch ? remoteSearch.value : localQuery;
    const setQuery = remoteSearch ? remoteSearch.onChange : setLocalQuery;

    const options: AttributeOptionItem[] =
        optionItems ??
        (attribute.options ?? []).map((option) => ({
            id: getOptionSelectionKey(option.value, option.unitCode),
            label: formatAttributeOptionLabel({
                value: option.value,
                unitCode: option.unitCode,
                unitName: option.unitName,
                type: attribute.type
            }),
            value: optionValueToString(option.value),
            unitCode: option.unitCode
        }));

    const selectedValues = selection.values[attribute.slug] ?? [];
    const selectedIds = new Set(selectedValues.map((item) => getOptionSelectionKey(item.value, item.unitCode)));
    const selectedOptions = [
        ...(selectedOptionItems ?? []),
        ...options.filter((item) => !selectedOptionItems?.some((pinned) => pinned.id === item.id))
    ].filter((option) => selectedIds.has(option.id));
    const normalizedQuery = query.trim().toLowerCase();
    const filteredOptions =
        normalizedQuery && !remoteSearch ? options.filter((option) => option.label.toLowerCase().includes(normalizedQuery)) : options;

    if (!options.length && !remoteSearch) {
        return null;
    }

    const showSearch = Boolean(remoteSearch) || options.length > SEARCH_OPTION_THRESHOLD;
    const draftOptions = draft ? { draft: true as const } : undefined;

    const handleToggle = (option: AttributeOptionItem) => {
        const isSelected = selectedIds.has(option.id);
        const nextValues = isSelected
            ? selectedValues.filter((item) => getOptionSelectionKey(item.value, item.unitCode) !== option.id)
            : [...selectedValues, { value: option.value, unitCode: option.unitCode }];

        onSetValues(attribute.slug, nextValues, draftOptions);
    };

    return (
        <div className='space-y-3'>
            {showSearch ? (
                <TextField
                    fullWidth
                    size='small'
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={ATTRIBUTE_FILTER_COPY.searchOptions}
                    InputProps={{
                        endAdornment: <SearchNormal1 size={16} color='currentColor' className='shrink-0 text-blackMediumEmphasis' />
                    }}
                />
            ) : null}

            {selectedOptions.length > 0 && !(remoteSearch && normalizedQuery) ? (
                <div className='space-y-2'>
                    <div className='flex items-center justify-between gap-2'>
                        <Typography variant='caption' color='primary' className='font-semibold'>
                            {ATTRIBUTE_FILTER_COPY.selectedCount(selectedOptions.length)}
                        </Typography>
                        <Button
                            variant='text'
                            color='primary'
                            className='min-w-0 px-1 py-0 text-xs'
                            onClick={() => onSetValues(attribute.slug, [], draftOptions)}>
                            {ATTRIBUTE_FILTER_COPY.clearAttribute}
                        </Button>
                    </div>
                    <div className='flex flex-wrap gap-1.5'>
                        {selectedOptions.map((option) => (
                            <button
                                key={option.id}
                                type='button'
                                onClick={() => handleToggle(option)}
                                className='inline-flex max-w-full cursor-pointer items-center gap-1 rounded-full border-0 bg-primary/10 px-2.5 py-1
                                    text-primary outline-none transition-colors hover:bg-primary/15'>
                                <Typography variant='caption' component='span' className='truncate font-semibold text-primary'>
                                    {option.label}
                                </Typography>
                                <CloseCircle size={14} color='currentColor' variant='Bold' className='shrink-0' />
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            <ScrollFade scrollerClassName='nice-scroll max-h-52 space-y-1 overflow-y-auto pe-1'>
                {remoteSearch?.isLoading && !filteredOptions.length ? (
                    <Skeleton variant='rounded' width='100%' height={72} />
                ) : filteredOptions.length ? (
                    filteredOptions.map((option) => {
                        const isSelected = selectedIds.has(option.id);

                        return (
                            <div
                                key={option.id}
                                className={`rounded-lg px-2 py-1 transition-colors ${isSelected ? 'bg-primary/10' : 'hover:bg-N50'}`}>
                                <CheckBox
                                    id={`attribute-filter-${attribute.slug}-${option.id}`}
                                    size='small'
                                    checked={isSelected}
                                    onChange={() => handleToggle(option)}
                                    typographyVariant='body2'
                                    className='w-full'
                                    label={
                                        <span className={`ms-1 ${isSelected ? 'font-semibold text-primary' : 'text-blackHighEmphasis'}`}>
                                            {option.label}
                                        </span>
                                    }
                                />
                            </div>
                        );
                    })
                ) : (
                    <Typography variant='caption' color='blackMediumEmphasis' className='block py-3 text-center'>
                        {ATTRIBUTE_FILTER_COPY.noOptions}
                    </Typography>
                )}
            </ScrollFade>
        </div>
    );
};
